import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import {
  useSocialExportGetYoutubeCapabilities,
  useSocialExportGetYoutubePlaylists,
} from '@mi-front/api-v2';
import { YoutubePlaylistsAutocomplete } from './YoutubePlaylistsAutocomplete';
import { YoutubePlaylistsNoPermissionsAlert } from './YoutubePlaylistsNoPermissionsAlert';
import { YoutubePlaylistsRefreshLink } from './YoutubePlaylistsRefreshLink';

type Props = {
  connectionId: string;
};

/**
 * Multi-select field for YouTube playlists on a connection.
 * Disabled when OAuth credentials lack playlist management scope.
 */
const MAX_PLAYLISTS = 10;

export function YoutubePlaylistsField({ connectionId }: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: youtubeCapabilities, isLoading: isLoadingYoutubeCapabilities } =
    useSocialExportGetYoutubeCapabilities(connectionId, {
      query: { enabled: !!connectionId },
    });
  const canManagePlaylists =
    !isLoadingYoutubeCapabilities && (youtubeCapabilities?.canManagePlaylists ?? false);
  const showNoPermissionsMessage = !isLoadingYoutubeCapabilities && !canManagePlaylists;

  const playlistsQuery = useSocialExportGetYoutubePlaylists(connectionId, {
    query: {
      enabled: !!connectionId && canManagePlaylists,
    },
  });

  const playlists = playlistsQuery.data?.data ?? [];
  const isLoading = playlistsQuery.isLoading || isRefreshing;

  const sortedPlaylists = useMemo(() => {
    return [...playlists].sort((a, b) => a.title.localeCompare(b.title));
  }, [playlists]);

  const helperText = (
    <YoutubePlaylistsRefreshLink
      connectionId={connectionId}
      isLoading={isLoading}
      onRefreshStateChange={setIsRefreshing}
    />
  );

  const fieldHelperText = canManagePlaylists ? helperText : undefined;

  return (
    <Stack spacing={1}>
      <YoutubePlaylistsAutocomplete
        playlists={sortedPlaylists}
        isLoading={isLoading}
        maxSelected={MAX_PLAYLISTS}
        helperText={fieldHelperText}
        disabled={!canManagePlaylists}
      />
      {showNoPermissionsMessage && <YoutubePlaylistsNoPermissionsAlert />}
    </Stack>
  );
}
